import { fetchUserData } from '~/queries'

/**
 * Keeps a session alive while somebody is sitting on it (BOAT-1898).
 *
 * The server slides the expiry window on every authenticated request, so a
 * customer who is clicking about never comes near it. The gap this closes is
 * the one where they are on the page but not asking for anything — which is
 * what a checkout is, and what a checkout is for long enough to matter. One
 * request when the session is nearly out is enough to push it back out again.
 *
 * Only when the tab is actually in front of them: refreshing a session for a
 * browser that has been minimised since Tuesday is how the thirty-day window
 * quietly becomes forever, which is the property this epic just removed. The
 * ceiling on the server cannot be slid at all, so a session that has genuinely
 * run its course ends here whatever this does — it just ends with a warning and
 * a login box rather than a failed request.
 *
 * Also checked when a tab is brought back to the front: a laptop that was shut
 * for a week fires no interval, and the first thing its owner should see is the
 * truth about their session rather than the state it was in when the lid went
 * down.
 */
const CHECK_EVERY_MS = 60 * 1000

export default defineNuxtPlugin((nuxtApp) => {
  if (typeof window === 'undefined') return

  nuxtApp.hook('app:mounted', () => {
    const session = useSessionExpiry()
    let running = false

    const check = async () => {
      if (running) return

      running = true

      try {
        if (session.isExpired()) {
          session.expired()

          return
        }

        if (!session.needsKeepAlive()) return

        if (document.visibilityState !== 'visible') return

        // An ordinary authenticated call. It slides the window server-side and
        // comes back with the new expiry on the response header, which is all
        // this needs; a dedicated endpoint would be the same request with more
        // to maintain. A failure is already handled — a 401 goes through the
        // central expiry path, and anything else is a blip worth ignoring.
        await fetchUserData().catch(() => undefined)

        // Only now, once a refresh has been given its chance. If the session is
        // still nearly out, it is the ceiling rather than idleness, and the
        // customer genuinely does have to sign in again.
        session.warnIfEnding()
      } finally {
        running = false
      }
    }

    window.setInterval(() => void check(), CHECK_EVERY_MS)

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') void check()
    })

    void check()
  })
})
